import type { ClientAdminsClientNewsletter } from '@fukuri-admins/apis/client/gen/model';
import type { PUBLISH_TYPE } from '../../constants';

type ExistingAttachment = {
  blobSignedId: string;
  name: string;
  url: string;
};

type EditInternalNewsInitialData = {
  title: string;
  body: string;
  /** 既存のメイン画像が存在するかのフラグ（URL管理用） */
  hadMainImage: boolean;
  /** 既存のメイン画像 URL（編集画面での初期プレビュー表示用） */
  mainImageUrl: string | undefined;
  isPublished: boolean;
  publishMode: (typeof PUBLISH_TYPE)[keyof typeof PUBLISH_TYPE];
  scheduledDate: Date | undefined;
  scheduledTime: string | undefined;
  existingAttachments: ExistingAttachment[];
};

/**
 * API レスポンスをフォーム初期値に変換する
 */
export const convertToInitialData = (
  response: ClientAdminsClientNewsletter
): EditInternalNewsInitialData => {
  const publishAtMs = response.publish_at * 1000;
  const isScheduled = publishAtMs > Date.now();

  const scheduledDate = isScheduled ? new Date(publishAtMs) : undefined;
  const scheduledTime =
    isScheduled && scheduledDate
      ? `${String(scheduledDate.getHours()).padStart(2, '0')}:${String(scheduledDate.getMinutes()).padStart(2, '0')}`
      : undefined;

  const mainImageUrl = response.main_image?.url ?? undefined;
  const hadMainImage = mainImageUrl != null;

  const existingAttachments: ExistingAttachment[] = (response.files ?? [])
    .filter(
      (f): f is typeof f & { blob_signed_id: string; url: string } =>
        f.blob_signed_id != null && f.url != null
    )
    .map(f => ({
      blobSignedId: f.blob_signed_id,
      name: f.filename ?? decodeURIComponent(f.url.split('/').pop()?.split('?')[0] ?? f.url),
      url: f.url,
    }));

  return {
    title: response.title,
    body: response.body,
    hadMainImage,
    mainImageUrl,
    isPublished: response.is_published,
    publishMode: isScheduled ? 'scheduled' : 'immediately',
    scheduledDate,
    scheduledTime,
    existingAttachments,
  };
};
